import React, { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { getCountryCallingCode } from 'libphonenumber-js';
import { getCountryOptions } from '../../api/codelist/codeListsSlice';

// Generate phone country codes from API country list
const usePhoneCountryCodes = () => {
  const countryOptions = useSelector(getCountryOptions);

  return useMemo(() => {
    const phoneCountryCodes = [];

    countryOptions.forEach((country) => {
      try {
        // country.code is the ISO country code (e.g., 'AE', 'US')
        const callingCode = getCountryCallingCode(country.code);
        phoneCountryCodes.push({
          code: `+${callingCode}`,
          label: country.label,
          isoCode: country.code, // Store ISO code for searching
        });
      } catch (error) {
        // Skip countries without valid calling codes
        console.warn(`Failed to get calling code for ${country.code}:`, error);
      }
    });

    // Sort by country name for better UX
    return phoneCountryCodes.sort((a, b) => a.label.localeCompare(b.label));
  }, [countryOptions]);
};

const PhoneCountryCodeSelect = ({
  name,
  label = 'Country Code',
  control,
  errors,
  readOnly = false,
  disabled,
  onChange,
}) => {
  const phoneCountryCodes = usePhoneCountryCodes();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        return (
          <Autocomplete
            {...field}
            id={name}
            data-testid={`${name}-select`}
            options={phoneCountryCodes}
            readOnly={readOnly}
            getOptionLabel={(option) => (option?.code ? `${option.code} ${option.label}` : '')}
            filterOptions={(options, { inputValue }) => {
              const searchValue = inputValue.toLowerCase();
              return options.filter((option) => {
                const phoneCodeMatch = option.code?.toLowerCase().includes(searchValue);
                const labelMatch = option.label?.toLowerCase().includes(searchValue);
                const isoCodeMatch = option.isoCode?.toLowerCase().includes(searchValue);
                return phoneCodeMatch || labelMatch || isoCodeMatch;
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                data-testid={`${name}-input`}
                label={label}
                error={!!get(errors, name, null)}
                helperText={get(errors, name, {})?.message}
              />
            )}
            isOptionEqualToValue={(option, value) => option?.code === value?.code}
            onChange={(_, value) => {
              field.onChange(value);
              onChange?.(value);
            }}
            value={field.value || null}
            disabled={disabled}
          />
        );
      }}
    />
  );
};

export default PhoneCountryCodeSelect;
